import Logo from "@assets/Logo.svg"
import PuzzelDonut from "@assets/puzzel_donut.svg"
import {
  HTTP_STATUS_FORBIDDEN,
  handleLogout,
  isLoggedOutError,
  startLogin,
} from "@auth/login"
import { meQuery } from "@auth/me.query"
import { SolutionChooser } from "@auth/SolutionChooser"
import { useMe } from "@auth/useMe"
import { Link, Outlet, createFileRoute } from "@tanstack/react-router"
import { Box } from "@ui/box"
import { Button } from "@ui/Button"
import { CollapsibleSidebar } from "@ui/CollapsibleSidebar"
import { Flex } from "@ui/flex"
import { Image } from "@ui/Image"
import { analytics } from "@utils/analytics"
import { ResponseError } from "@utils/request"
import type { FC, MouseEvent } from "react"
import {
  MdLink,
  MdLogout,
  MdOutlineSubtitles,
  MdStream,
  MdVisibility,
  MdWebhook,
} from "react-icons/md"
import { RiShieldFlashFill } from "react-icons/ri"
import { css } from "styled-system/css"

const AuthenticatedLayout: FC = () => {
  const user = useMe()
  const { hasSuperpowers } = user

  const trackNavClick = (event: MouseEvent<HTMLAnchorElement>): void => {
    analytics.event("main-menu__nav-item-clicked", {
      href: event.currentTarget.getAttribute("aria-label") ?? "",
    })
  }

  return (
    <Flex
      height="viewport"
      overflow="hidden"
      backgroundColor="[#F5F5F5]"
    >
      <CollapsibleSidebar.Root
        width="[200px]"
        minWidth="md"
      >
        <Flex
          flexDirection="column"
          justifyContent="space-between"
          height="full"
          width="full"
        >
          <Flex
            flexDirection="column"
            fontStyle="italic"
            width="full"
          >
            <Link
              to="/base-modules"
              aria-label="Logo"
              onClick={trackNavClick}
            >
              <img
                src={Logo}
                alt="Logo"
              />
            </Link>
            <Box marginTop="lg">
              <Box marginY>
                <SolutionChooser />
              </Box>
              <CollapsibleSidebar.Item asChild>
                <Link
                  to="/base-modules"
                  activeOptions={{ exact: true }}
                  aria-label="Base modules"
                  onClick={trackNavClick}
                >
                  <Flex
                    alignItems="center"
                    gap="sm"
                  >
                    <MdStream size={16} /> Base modules
                  </Flex>
                  <CollapsibleSidebar.ActiveDot />
                </Link>
              </CollapsibleSidebar.Item>
            </Box>
            <CollapsibleSidebar.Item asChild>
              <Link
                to="/live-summary"
                activeOptions={{ exact: true }}
                aria-label="Live summary"
                onClick={trackNavClick}
              >
                <Flex
                  alignItems="center"
                  gap="sm"
                >
                  <MdOutlineSubtitles size={16} /> Live summary
                </Flex>
                <CollapsibleSidebar.ActiveDot />
              </Link>
            </CollapsibleSidebar.Item>
            <CollapsibleSidebar.Item asChild>
              <Link
                to="/webhooks"
                activeOptions={{ exact: true }}
                aria-label="Webhooks"
                onClick={trackNavClick}
              >
                <Flex
                  alignItems="center"
                  gap="sm"
                >
                  <MdWebhook size={16} /> Webhooks
                </Flex>
                <CollapsibleSidebar.ActiveDot />
              </Link>
            </CollapsibleSidebar.Item>
            <CollapsibleSidebar.Item asChild>
              <Link
                to="/api-tokens"
                activeOptions={{ exact: true }}
                aria-label="API tokens"
                onClick={trackNavClick}
              >
                <Flex
                  alignItems="center"
                  gap="sm"
                >
                  <MdLink size={16} /> API tokens
                </Flex>
                <CollapsibleSidebar.ActiveDot />
              </Link>
            </CollapsibleSidebar.Item>
            {hasSuperpowers && (
              <CollapsibleSidebar.Item asChild>
                <Link
                  to="/widget-preview"
                  activeOptions={{ exact: true }}
                  aria-label="Widget preview"
                  onClick={trackNavClick}
                >
                  <Flex
                    alignItems="center"
                    gap="sm"
                  >
                    <MdVisibility size={16} /> Widget preview
                  </Flex>
                  <CollapsibleSidebar.ActiveDot />
                </Link>
              </CollapsibleSidebar.Item>
            )}
          </Flex>
          <Flex
            alignItems="center"
            justifyContent="space-between"
          >
            {hasSuperpowers && (
              <Link
                to="/superpowers"
                aria-label="Superpowers"
                onClick={trackNavClick}
                className={css({
                  color: "primary",
                  transition: "[transform 700ms ease]",
                  _hover: { transform: "scale(1.7)" },
                })}
              >
                <RiShieldFlashFill size={16} />
              </Link>
            )}
            <Button
              fontSize="small"
              variant="ghost"
              onClick={() => {
                void handleLogout()
              }}
            >
              <MdLogout />
              Log out
            </Button>
          </Flex>
        </Flex>
      </CollapsibleSidebar.Root>
      <Box
        flex="1"
        position="relative"
        overflow="hidden"
      >
        <Box
          position="absolute"
          bottom="[0]"
          left="[0]"
          right="[0]"
          pointerEvents="none"
        >
          <Image
            src={PuzzelDonut}
            //Screen readers skip decorative stuff.
            role="presentation"
            width="full"
            display="block"
          />
        </Box>
        <Box
          height="full"
          maxWidth="[1218px]"
          marginInline="auto"
          overflowY="auto"
          borderInlineWidth="[2px]"
          borderColor="surface"
          background="[linear-gradient(114deg, rgba(255, 255, 255, 0.60) 2.72%, rgba(255, 255, 255, 0.80) 100.25%)]"
          backdropFilter="[blur(10px)]"
        >
          <Outlet />
        </Box>
      </Box>
    </Flex>
  )
}

// beforeLoad is run on every page change
// we should only identify once
let userIdentified = false
export const Route = createFileRoute("/_authenticated")({
  // Ensure the user is authenticated before any page under this layout renders.
  // A "NoAccess" 403 means no session → send them through SSO; any other error
  // bubbles to the root error UI.
  beforeLoad: async ({ context }) => {
    try {
      const user = await context.queryClient.ensureQueryData(meQuery)
      if (user.solutions.length === 0) {
        // A user with no solutions comes back as a 200 with an empty `solutions`
        // array. Treat it as the same "no solutions" error the backend
        // returns for a session without a valid solution, so the root error UI
        // renders a single, consistent screen for both.
        throw new ResponseError({
          error: "Forbidden",
          statusCode: HTTP_STATUS_FORBIDDEN,
          message: "No solutions attached to account",
          detail: "Missing required solutionUid",
          title: "You are not authorized to do this operation",
          traceId: "",
        })
      }

      if (!userIdentified) {
        analytics.identify(user)
        userIdentified = true
      }

      return { user }
    } catch (error) {
      if (isLoggedOutError(error)) {
        await startLogin()
      }
      throw error
    }
  },
  component: AuthenticatedLayout,
})
